import { Dialog } from '@base-ui/react/dialog'
import { Eye, EyeOff, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface WidgetMeta {
  id: string
  label: string
  description?: string
  group: 'indicator' | 'panel'
}

interface DashboardEditDrawerProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly widgets: readonly WidgetMeta[]
  readonly isHidden: (id: string) => boolean
  readonly onToggle: (id: string) => void
  readonly onReset: () => void
}

const GROUP_LABEL: Record<WidgetMeta['group'], string> = {
  indicator: 'Indicadores',
  panel: 'Painéis',
}

export function DashboardEditDrawer({
  open,
  onClose,
  widgets,
  isHidden,
  onToggle,
  onReset,
}: DashboardEditDrawerProps) {
  const grouped = widgets.reduce<Record<WidgetMeta['group'], WidgetMeta[]>>(
    (acc, w) => {
      acc[w.group].push(w)
      return acc
    },
    { indicator: [], panel: [] },
  )

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col border-l border-border bg-background shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <Dialog.Title className="text-base font-semibold text-foreground">
                Personalizar dashboard
              </Dialog.Title>
              <p className="text-xs text-muted-foreground">
                Esconda o que não precisa. Fica salvo neste navegador.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {(Object.keys(grouped) as WidgetMeta['group'][]).map((group) => {
              const items = grouped[group]
              if (items.length === 0) return null
              return (
                <section key={group} className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {GROUP_LABEL[group]}
                  </h3>
                  <ul className="space-y-1">
                    {items.map((w) => {
                      const hidden = isHidden(w.id)
                      return (
                        <li key={w.id}>
                          <button
                            type="button"
                            onClick={() => onToggle(w.id)}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors',
                              hidden
                                ? 'border-dashed border-border bg-muted/30 text-muted-foreground'
                                : 'border-border bg-card hover:bg-muted/50',
                            )}
                            aria-pressed={!hidden}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">{w.label}</p>
                              {w.description && (
                                <p className="text-xs text-muted-foreground">{w.description}</p>
                              )}
                            </div>
                            <span className={hidden ? 'text-muted-foreground' : 'text-primary'}>
                              {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
            <Button variant="ghost" size="sm" onClick={onReset}>
              <RotateCcw size={14} />
              Restaurar tudo
            </Button>
            <Button size="sm" onClick={onClose}>Concluído</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
